import React, { useRef, forwardRef, useImperativeHandle, useEffect } from 'react';

// Simple captcha component that can work with or without the react-google-recaptcha library
const Captcha = forwardRef(({ siteKey, onChange, onExpired, onError, theme = 'light', size = 'normal' }, ref) => {
  const captchaRef = useRef(null);

  useImperativeHandle(ref, () => ({
    reset: () => {
      if (captchaRef.current && window.grecaptcha) {
        window.grecaptcha.reset(captchaRef.current);
      }
    },
    execute: () => {
      if (captchaRef.current && window.grecaptcha) {
        window.grecaptcha.execute(captchaRef.current);
      }
    },
    getValue: () => {
      if (captchaRef.current && window.grecaptcha) {
        return window.grecaptcha.getResponse(captchaRef.current);
      }
      return null;
    }
  }));

  // Load reCAPTCHA script with explicit render parameter
  const loadRecaptchaScript = () => {
    if (window.grecaptcha) {
      console.log('reCAPTCHA script already loaded');
      return Promise.resolve();
    }
    
    console.log('Loading reCAPTCHA script with explicit render...');
    return new Promise((resolve, reject) => {
      // Create a global callback function
      window.onRecaptchaLoad = () => {
        console.log('reCAPTCHA onload callback triggered');
        resolve();
      };
      
      const script = document.createElement('script');
      script.src = 'https://www.google.com/recaptcha/api.js?onload=onRecaptchaLoad&render=explicit';
      script.async = true;
      script.defer = true;
      script.onerror = () => {
        console.error('Failed to load reCAPTCHA script');
        reject(new Error('Failed to load reCAPTCHA'));
      };
      document.head.appendChild(script);
    });
  };

  // Initialize reCAPTCHA with explicit render approach
  const initializeRecaptcha = async () => {
    try {
      console.log('Loading reCAPTCHA script...');
      await loadRecaptchaScript();
      console.log('reCAPTCHA script loaded successfully');
      
      // Direct render approach - no need for ready() callback with explicit render
      const renderCaptcha = () => {
        return new Promise((resolve, reject) => {
          let attempts = 0;
          const maxAttempts = 20; // 2 seconds max
          
          const checkAndRender = () => {
            attempts++;
            
            if (window.grecaptcha && window.grecaptcha.render) {
              console.log('grecaptcha.render is available, rendering widget...');
              try {
                if (captchaRef.current && !captchaRef.current.hasChildNodes()) {
                  console.log('Rendering reCAPTCHA widget with siteKey:', siteKey);
                  const widgetId = window.grecaptcha.render(captchaRef.current, {
                    sitekey: siteKey,
                    callback: onChange,
                    'expired-callback': onExpired,
                    'error-callback': onError,
                    theme: theme,
                    size: size
                  });
                  console.log('reCAPTCHA widget rendered successfully, widgetId:', widgetId);
                  resolve(widgetId);
                } else {
                  console.log('Captcha ref not available or already has children');
                  reject(new Error('Captcha ref not available'));
                }
              } catch (renderError) {
                console.error('Error rendering reCAPTCHA:', renderError);
                reject(renderError);
              }
            } else if (attempts >= maxAttempts) {
              console.error('Timeout waiting for grecaptcha.render');
              reject(new Error('reCAPTCHA timeout - render not available'));
            } else {
              console.log(`Waiting for grecaptcha.render... (attempt ${attempts}/${maxAttempts})`);
              console.log('Available:', {
                grecaptcha: !!window.grecaptcha,
                render: !!(window.grecaptcha && window.grecaptcha.render)
              });
              setTimeout(checkAndRender, 100);
            }
          };
          
          checkAndRender();
        });
      };
      
      await renderCaptcha();
      
    } catch (error) {
      console.error('Failed to initialize reCAPTCHA:', error);
      if (onError) onError(error);
    }
  };

  // Initialize on mount
  useEffect(() => {
    console.log('Captcha component mounted with siteKey:', siteKey);
    if (siteKey) {
      console.log('Initializing reCAPTCHA...');
      initializeRecaptcha();
    } else {
      console.log('No siteKey provided to Captcha component');
    }
  }, [siteKey]);

  // Fallback UI if no site key is provided
  if (!siteKey) {
    return (
      <div className="p-4 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
        <div className="text-center text-gray-600">
          <p className="text-sm font-medium">reCAPTCHA Configuration Required</p>
          <p className="text-xs mt-1">Please provide the reCAPTCHA site key to enable verification.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="recaptcha-container">
      <div ref={captchaRef} className="g-recaptcha"></div>
    </div>
  );
});

Captcha.displayName = 'Captcha';

export default Captcha;
